/**
 * Разбор шаблона пропусков — общий у «пропусков в тексте» (`gap_fill`) и ячеек
 * «Дополни таблицу» (`table_fill`, 13.10.2026). Перенесён сюда из blockRenderers.tsx
 * без изменений, чтобы им пользовались и плеер, и чистая логика таблицы
 * (lib/tableFill.ts). Близнецы на сервере — gapAnswers (progress-service
 * block_grader.go) и первыеОтветы (courses-service block_compat.go).
 */

export type GapSegment =
  | { kind: 'text'; text: string }
  | { kind: 'gap'; answers: string[]; distractors?: string[] };

// Parse a gap template into flowing text/gap segments. Two authoring syntaxes
// are accepted and may be mixed in one template:
//   [[answer|alt]]   — double brackets; "|" separates several ACCEPTED answers
//                      (synonyms). Wrong options for dropdown/drag come from the
//                      block's shared `distractors` field.
//   [a / b* / c]     — single brackets, Edvibe-style; "/" separates the options,
//                      "*" marks the correct one(s), and every other option is
//                      that gap's OWN distractor. With no "*", the first option
//                      is taken as correct.
// A single [...] is treated as a gap ONLY when it contains "/" or "*"; a bare
// "[sic]" / "[1]" in the prose stays literal text and never becomes a blank.
export function parseGapTemplate(template: string): GapSegment[] {
  const out: GapSegment[] = [];
  const re = /\[\[([^\]]*)\]\]|\[([^[\]]*[/*][^[\]]*)\]/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(template)) !== null) {
    if (m.index > last) out.push({ kind: 'text', text: template.slice(last, m.index) });
    if (m[1] !== undefined) {
      const answers = m[1].split('|').map((s) => s.trim()).filter(Boolean);
      out.push({ kind: 'gap', answers: answers.length ? answers : [''] });
    } else {
      const opts = (m[2] ?? '').split('/').map((s) => s.trim()).filter(Boolean);
      const starred = opts.filter((o) => o.endsWith('*')).map((o) => o.slice(0, -1).trim());
      const correct = (starred.length ? starred : opts.slice(0, 1)).filter(Boolean);
      const correctSet = new Set(correct.map((a) => a.toLowerCase()));
      const distractors = opts
        .map((o) => (o.endsWith('*') ? o.slice(0, -1).trim() : o))
        .filter((o) => o && !correctSet.has(o.toLowerCase()));
      out.push({ kind: 'gap', answers: correct.length ? correct : [''], distractors });
    }
    last = re.lastIndex;
  }
  if (last < template.length) out.push({ kind: 'text', text: template.slice(last) });
  return out;
}
